/*
 * XGENAUTO pickup discount (cart page + cart drawer)
 *
 * - Asks the pickup Worker which cart lines qualify (in stock at the studio or pre-order).
 * - Shows the checkbox only when at least one line qualifies, with a note on each line.
 * - When ticked, applies the cart's PICKUP10- code and sets the "Pickup Discount: Yes"
 *   cart attribute in the same request (HideShip uses that attribute to hide shipping).
 * - Re-checks after every cart change (add, remove, quantity, clear) so the code always
 *   matches what is in the cart, and holds the checkout button until the check is done.
 *
 * Config comes from snippets/xga-pickup-discount.liquid (window.XGA_PICKUP).
 */
(function () {
  'use strict';

  var ATTR = 'Pickup Discount';
  var PREFIX = 'PICKUP10-';
  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  /* ---------------------------------------------------------------- */
  /* Pure logic (tested separately)                                    */
  /* ---------------------------------------------------------------- */

  function isPickupCode(code) {
    return String(code || '').toUpperCase().indexOf(PREFIX) === 0;
  }

  /**
   * Decide what the cart should look like after a sync.
   * cart:    /cart.js response
   * result:  Worker response ({ lines, anyEligible, code })
   * enabled: whether the customer wants the pickup discount
   */
  function planUpdate(cart, result, enabled) {
    var applied = (cart.discount_codes || []).map(function (d) { return d.code; });
    var others = applied.filter(function (c) { return !isPickupCode(c); });

    var wantPickup = !!(enabled && result && result.anyEligible && result.code);
    var desiredCodes = wantPickup ? others.concat([result.code]) : others;
    var desiredAttr = wantPickup ? 'Yes' : '';
    var currentAttr = (cart.attributes && cart.attributes[ATTR]) || '';

    var codesChanged =
      applied.length !== desiredCodes.length ||
      desiredCodes.some(function (c) { return applied.indexOf(c) === -1; });

    return {
      needsUpdate: codesChanged || currentAttr !== desiredAttr,
      discount: desiredCodes.join(','),
      attr: desiredAttr,
      enabled: wantPickup,
      code: wantPickup ? result.code : null
    };
  }

  /** Did a theme request to /cart/update drop our code (customer removed the pill)? */
  function requestRemovesCode(bodyText, activeCode) {
    if (!activeCode || !bodyText) return false;
    var body;
    try { body = JSON.parse(bodyText); } catch (e) { return false; }
    if (!body || typeof body.discount !== 'string') return false;
    var codes = body.discount.split(',').map(function (c) { return c.trim().toUpperCase(); });
    return codes.indexOf(activeCode.toUpperCase()) === -1;
  }

  function pickupSavings(item) {
    return (item.line_level_discount_allocations || []).reduce(function (sum, a) {
      var title = a.discount_application && a.discount_application.title;
      return isPickupCode(title) ? sum + (a.amount || 0) : sum;
    }, 0);
  }

  if (typeof window === 'undefined' || window.__XGA_PICKUP_TEST__) {
    var api = { planUpdate: planUpdate, requestRemovesCode: requestRemovesCode, pickupSavings: pickupSavings };
    if (typeof module !== 'undefined') module.exports = api;
    if (typeof window !== 'undefined') window.__XGA_PICKUP_API__ = api;
    return;
  }

  /* ---------------------------------------------------------------- */
  /* Browser wiring                                                    */
  /* ---------------------------------------------------------------- */

  var CFG = window.XGA_PICKUP || {};
  if (!CFG.endpoint || window.__XGA_PICKUP_LOADED__) return;
  window.__XGA_PICKUP_LOADED__ = true;

  var nativeFetch = window.fetch.bind(window);
  var state = { cart: null, result: null, enabled: false, code: null, error: false };
  var syncing = null;
  var queued = null;
  var dirty = false;
  var userRemoved = false;
  var debounce = null;
  var leaving = false;
  var leaveTimer = null;

  function routes() {
    var r = window.routes || {};
    return {
      cart: (r.cart_url || '/cart') + '.js',
      update: (r.cart_update_url || '/cart/update') + '.js'
    };
  }

  function money(cents, currency) {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function escapeHtml(text) {
    return String(text).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ---------- watch the theme's cart requests ---------- */

  function noteCartRequest(url, method, bodyText) {
    // Once the customer is on the way to checkout, apps such as Ship Check edit the cart
    // (e.g. add package protection). Those never change the pickup code, so ignore them
    // instead of flickering the buttons.
    if (leaving) return false;
    if (!url || String(method || 'GET').toUpperCase() === 'GET') return false;
    if (!CART_MUTATION.test(String(url).split('#')[0])) return false;
    if (/\/cart\/update/.test(url) && requestRemovesCode(bodyText, state.code)) userRemoved = true;
    dirty = true;
    setBusy(true);
    return true;
  }

  function scheduleSync() {
    clearTimeout(debounce);
    debounce = setTimeout(function () { sync(); }, 250);
  }

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var method = (init && init.method) || (input && input.method) || 'GET';
    var body = init && typeof init.body === 'string' ? init.body : null;
    var watched = noteCartRequest(url, method, body);
    var p = nativeFetch(input, init);
    if (watched) p.then(scheduleSync, scheduleSync);
    return p;
  };

  var xhrOpen = XMLHttpRequest.prototype.open;
  var xhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__xgaMethod = method;
    this.__xgaUrl = url;
    return xhrOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function (body) {
    if (noteCartRequest(this.__xgaUrl, this.__xgaMethod, typeof body === 'string' ? body : null)) {
      this.addEventListener('loadend', scheduleSync);
    }
    return xhrSend.apply(this, arguments);
  };

  /* ---------- sync ---------- */

  function sync(opts) {
    if (syncing) {
      queued = opts || queued || {};
      return syncing;
    }
    setBusy(true);
    syncing = (async function () {
      var next = opts || {};
      try {
        do {
          queued = null;
          await runSync(next);
          next = queued || {};
        } while (queued);
        state.error = false;
      } catch (e) {
        console.warn('[pickup discount]', e);
        state.error = true;
      } finally {
        dirty = false;
        syncing = null;
        setBusy(false);
        render();
      }
    })();
    return syncing;
  }

  async function runSync(opts) {
    var cart = await (await nativeFetch(routes().cart, { headers: { Accept: 'application/json' } })).json();

    var enabled = typeof opts.enabled === 'boolean'
      ? opts.enabled
      : !!(cart.attributes && cart.attributes[ATTR] === 'Yes');
    if (userRemoved) {
      enabled = false;
      userRemoved = false;
    }

    // A pickup code Shopify reports as not applicable (usually expired after 24h):
    // ask the Worker for a fresh one instead of keeping the dead code.
    var staleCode = null;
    (cart.discount_codes || []).forEach(function (d) {
      if (isPickupCode(d.code) && d.applicable === false) staleCode = d.code;
    });

    var result = { lines: {}, anyEligible: false, code: null };
    if (cart.item_count > 0) {
      var res = await nativeFetch(CFG.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cartToken: cart.token,
          enabled: enabled,
          refresh: !!staleCode,
          staleCode: staleCode,
          lines: cart.items.map(function (i) {
            return { key: i.key, variantId: i.variant_id, quantity: i.quantity };
          })
        })
      });
      if (!res.ok) throw new Error('pickup service ' + res.status);
      result = await res.json();

      // If the Worker still hands back the dead code, drop it rather than leave
      // the customer with a ticked box and no discount.
      if (staleCode && result.code && result.code.toUpperCase() === staleCode.toUpperCase()) {
        console.warn('[pickup discount] Worker returned the expired code again');
        result.code = null;
        state.error = true;
      }
    }

    var plan = planUpdate(cart, result, enabled);
    if (plan.needsUpdate) {
      var attributes = {};
      attributes[ATTR] = plan.attr;
      cart = await updateCart({ discount: plan.discount, attributes: attributes });
      // Shopify gives each line a new key once a discount is applied or removed, so
      // re-file the eligibility results under the updated cart's keys (by variant).
      result = rekeyLines(result, cart);
    }

    state.cart = cart;
    state.result = result;
    state.enabled = plan.enabled;
    state.code = plan.code;
  }

  function rekeyLines(result, cart) {
    if (!result || !result.lines || !cart || !cart.items) return result;
    var byVariant = {};
    Object.keys(result.lines).forEach(function (key) {
      byVariant[String(key).split(':')[0]] = result.lines[key];
    });
    var lines = {};
    cart.items.forEach(function (item) {
      var info = result.lines[item.key] || byVariant[String(item.variant_id)];
      if (info) lines[item.key] = info;
    });
    var out = {};
    Object.keys(result).forEach(function (k) { out[k] = result[k]; });
    out.lines = lines;
    return out;
  }

  async function updateCart(payload) {
    var sections = sectionsToRender();
    payload.sections = sections.map(function (s) { return s.id; });
    var res = await nativeFetch(routes().update, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!res.ok) throw new Error('cart update ' + res.status);
    var data = await res.json();
    renderSections(data, sections);
    return data;
  }

  /* ---------- re-render the theme's cart after our update ---------- */

  function sectionsToRender() {
    var list = [];
    if (document.querySelector('cart-drawer')) {
      list.push({ id: 'cart-drawer', kind: 'drawer' });
      // Shrine's drawer renderer also expects the cart icon section in the response.
      list.push({ id: 'cart-icon-bubble', kind: 'section' });
    }
    var items = document.querySelector('cart-items');
    var itemsSection = items && items.closest('.shopify-section');
    if (itemsSection) list.push({ id: itemsSection.id.replace('shopify-section-', ''), kind: 'section' });
    var footer = document.getElementById('main-cart-footer');
    if (footer && footer.dataset.id) list.push({ id: footer.dataset.id, kind: 'section' });
    return list;
  }

  function renderSections(data, sections) {
    if (!data || !data.sections) return;
    var parser = new DOMParser();

    sections.forEach(function (s) {
      var html = data.sections[s.id];
      if (!html) return;
      var doc = parser.parseFromString(html, 'text/html');

      if (s.kind === 'drawer') {
        var drawer = document.querySelector('cart-drawer');
        var isOpen = drawer && (drawer.classList.contains('active') || drawer.classList.contains('animate'));
        if (isOpen && typeof drawer.renderContents === 'function') {
          drawer.renderContents(data);
          return;
        }
        var fresh = doc.querySelector('#CartDrawer');
        var current = document.getElementById('CartDrawer');
        if (fresh && current) {
          current.innerHTML = fresh.innerHTML;
          var overlay = current.querySelector('#CartDrawer-Overlay');
          if (overlay && drawer && typeof drawer.close === 'function') {
            overlay.addEventListener('click', drawer.close.bind(drawer));
          }
        }
        return;
      }

      var target = document.getElementById('shopify-section-' + s.id);
      var source = doc.querySelector('.shopify-section') || doc.body;
      if (target && source) target.innerHTML = source.innerHTML;
    });
  }

  /* ---------- checkout guard ---------- */

  var CHECKOUT_SELECTOR = '#checkout, #CartDrawer-Checkout, button[name="checkout"], input[name="checkout"]';

  function setBusy(busy) {
    document.querySelectorAll(CHECKOUT_SELECTOR).forEach(function (btn) {
      if (busy) {
        if (!btn.disabled) {
          btn.disabled = true;
          btn.dataset.xgaHeld = '1';
        }
        btn.setAttribute('aria-busy', 'true');
      } else {
        if (btn.dataset.xgaHeld) {
          btn.disabled = false;
          delete btn.dataset.xgaHeld;
        }
        btn.removeAttribute('aria-busy');
      }
    });
    document.querySelectorAll('.xga-pickup').forEach(function (el) {
      el.classList.toggle('is-busy', !!busy);
    });
  }

  /* ---------- checkout click: spinner, then stay quiet until checkout opens ---------- */

  function showCheckoutLoading(on) {
    var buttons = document.querySelectorAll(CHECKOUT_SELECTOR + ', .xga-sticky-checkout .button');
    buttons.forEach(function (btn) {
      btn.classList.toggle('xga-checkout-loading', !!on);
      var spin = btn.querySelector(':scope > .xga-btn-spinner');
      if (on && !spin) {
        spin = document.createElement('span');
        spin.className = 'xga-btn-spinner';
        spin.setAttribute('aria-hidden', 'true');
        btn.appendChild(spin);
      } else if (!on && spin) {
        spin.remove();
      }
      if (on) btn.setAttribute('aria-busy', 'true');
      else if (!syncing && !dirty) btn.removeAttribute('aria-busy');
    });
  }

  // "Leaving" = the customer is on the way to checkout. Cart edits made by apps from here
  // on (Ship Check adding package protection) are ignored instead of re-checked.
  function armLeaving(ms) {
    leaving = true;
    clearTimeout(debounce);
    // Safety net: if checkout doesn't open (an app error, a blocked redirect), recover.
    clearTimeout(leaveTimer);
    leaveTimer = setTimeout(stopLeaving, ms);
  }

  function lockCheckout(on) {
    document.querySelectorAll(CHECKOUT_SELECTOR + ', .xga-sticky-checkout .button').forEach(function (b) {
      b.classList.toggle('xga-checkout-locked', !!on);
    });
  }

  function stopLeaving() {
    clearTimeout(leaveTimer);
    if (!leaving) return;
    leaving = false;
    lastClickAt = 0;
    lockCheckout(false);
    showCheckoutLoading(false);
    sync();
  }

  // Back button from checkout restores this page from the browser cache: reset it.
  window.addEventListener('pageshow', function (e) { if (e.persisted) stopLeaving(); });

  // Apps such as Ship Check start editing the cart as soon as checkout is pressed. Note the
  // press (pointer down / Enter) so those edits aren't mistaken for customer changes.
  // Nothing on the page changes at this point: iPhone Safari cancels a tap if the page
  // changes while the finger is still down. The spinner is added on the click instead.
  // If no click follows (finger slid off), the note is dropped after 1.5s.
  var PRESS_SELECTOR = CHECKOUT_SELECTOR + ', .xga-sticky-checkout .button';
  var lastClickAt = 0;
  function onPress(e) {
    var btn = e.target.closest && e.target.closest(PRESS_SELECTOR);
    if (!btn || btn.disabled || leaving || syncing || dirty) return;
    var pressedAt = Date.now();
    armLeaving(1500);
    setTimeout(function () {
      if (!leaving) return;
      if (lastClickAt >= pressedAt) armLeaving(8000);
      else stopLeaving();
    }, 1500);
  }
  window.addEventListener('pointerdown', onPress, true);
  window.addEventListener('keydown', function (e) {
    if (e.key === 'Enter' || e.key === ' ') onPress(e);
  }, true);

  // Window capture runs before any listener on the document, so this sees the click first.
  window.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest(CHECKOUT_SELECTOR);
    if (!btn) return;
    lastClickAt = Date.now();
    if (leaving) {
      // Pressed just now (or an app re-clicking): show the spinner and let the click through.
      armLeaving(8000);
      showCheckoutLoading(true);
      setTimeout(function () { if (leaving) lockCheckout(true); }, 0);
      return;
    }
    if (syncing || dirty) {
      // Customer got to checkout mid-update: finish the update first, then continue.
      e.preventDefault();
      e.stopImmediatePropagation();
      showCheckoutLoading(true);
      sync().then(function () {
        showCheckoutLoading(false);
        btn.click();
      });
      return;
    }
    armLeaving(8000);
    showCheckoutLoading(true);
    // Lock out repeat taps only after this click has gone through.
    setTimeout(function () { if (leaving) lockCheckout(true); }, 0);
  }, true);

  /* ---------- widget + line notes ---------- */

  function widgetHtml() {
    var r = state.result;
    var cart = state.cart;
    var savings = 0;
    if (state.enabled && cart) {
      cart.items.forEach(function (i) { savings += pickupSavings(i); });
    }
    return (
      '<label class="xga-pickup__label">' +
        '<input type="checkbox" class="xga-pickup__input"' + (state.enabled ? ' checked' : '') + '>' +
        '<span class="xga-pickup__text">' +
          '<strong>' + escapeHtml(CFG.heading || 'Pick Up My Order At XGENAUTO Los Angeles & Save 10%!') + '</strong>' +
          '<span class="xga-pickup__sub">Discount applies only to select eligible items in cart. Choose this option for local pickup only. Once your item(s) are ready for pickup our team will contact you to schedule your pickup appointment.</span>' +
        '</span>' +
      '</label>' +
      (state.enabled && savings > 0
        ? '<p class="xga-pickup__status">Pickup Discount Applied: You Saved ' + money(savings, cart.currency) + '!</p>'
        : '') +
      (state.error
        ? '<p class="xga-pickup__error">We could not update the pickup discount. Please refresh the page and try again.</p>'
        : '')
    );
  }

  function render() {
    if (typeof observer !== 'undefined') observer.disconnect();
    try {
      paint();
    } finally {
      if (typeof observer !== 'undefined') {
        observer.takeRecords();
        observe();
      }
    }
  }

  function paint() {
    var cart = state.cart;
    var r = state.result;
    var show = !!(cart && cart.item_count > 0 && r && r.anyEligible);

    // Checkbox above each checkout button (cart page + drawer).
    document.querySelectorAll('.cart__ctas').forEach(function (ctas) {
      var box = ctas.previousElementSibling;
      if (!box || !box.classList || !box.classList.contains('xga-pickup')) {
        box = document.createElement('div');
        box.className = 'xga-pickup';
        ctas.parentNode.insertBefore(box, ctas);
      }
      box.hidden = !show;
      if (show) box.innerHTML = widgetHtml();
    });

    // Note on each cart line, only once the customer has chosen pickup.
    document.querySelectorAll('.xga-pickup-note').forEach(function (n) { n.remove(); });
    if (!show || !state.enabled) return;
    cart.items.forEach(function (item, idx) {
      var info = r.lines[item.key];
      if (!info) return;
      var text;
      var cls = 'xga-pickup-note';
      if (info.eligible) {
        cls += ' is-eligible';
        text = info.reason === 'preorder'
          ? 'Pre-Order: 10% Pickup Discount Applied'
          : 'In Stock: 10% Pickup Discount Applied';
      } else {
        text = 'Ships To Store For Pickup: Not Eligible For Pickup Discount';
      }
      ['CartItem-' + (idx + 1), 'CartDrawer-Item-' + (idx + 1)].forEach(function (id) {
        var row = document.getElementById(id);
        var details = row && row.querySelector('.cart-item__details');
        if (!details) return;
        var note = document.createElement('p');
        note.className = cls;
        note.textContent = text;
        details.appendChild(note);
      });
    });
  }

  document.addEventListener('change', function (e) {
    if (!e.target.classList || !e.target.classList.contains('xga-pickup__input')) return;
    sync({ enabled: e.target.checked });
  });

  // The theme replaces cart markup after its own updates; put our UI back afterwards.
  // The observer is paused while we write our own markup so it can't trigger itself.
  var renderQueued = false;
  var observer = new MutationObserver(function () {
    if (renderQueued) return;
    renderQueued = true;
    requestAnimationFrame(function () {
      renderQueued = false;
      if (state.cart) render();
      if (leaving && lastClickAt) { showCheckoutLoading(true); lockCheckout(true); }
      else if (syncing || dirty) setBusy(true);
    });
  });
  function observe() { observer.observe(document.body, { childList: true, subtree: true }); }
  observe();

  function boot() { sync(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
